import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { Address } from '../address/Address'
import { Metadata, type MetadataType } from '../base/Metadata'
import { ShippingRate } from './ShippingRate'
import { ShippingPackage } from './ShippingPackage'
import type { OrderDeliveryConfirmationEnum } from '../order/OrderDeliveryConfirmationEnum'
import { ShippingCustoms } from './ShippingCustoms'
import { generateEntityId } from '../generateEntityId'
import type { ShippingGatewayProvider } from './ShippingGatewayProvider'
import GraphQLJSON from 'graphql-type-json'

@ObjectType()
@InputType('ShippingInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class Shipping {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ship') })
  _id: string

  @Field(() => ShippingRate)
  @Prop({ type: ShippingRate })
  rate: ShippingRate

  @Field(() => String)
  @Prop({ type: String })
  rateId: string

  @Field(() => String)
  @Prop({ type: String })
  shipmentId: string

  @Field(() => String)
  @Prop({ type: String })
  labelId: string

  @Field(() => String)
  @Prop({ type: String })
  status: string

  @Field(() => Float)
  @Prop({ type: Number })
  amount: number

  @Field(() => Float)
  @Prop({ type: Number })
  shippingCost: number

  @Field(() => Float)
  @Prop({ type: Number })
  insuranceCost: number

  @Field(() => Float)
  @Prop({ type: Number })
  confirmationCost: number

  @Field(() => Float)
  @Prop({ type: Number })
  otherCost: number

  @Field(() => Float)
  @Prop({ type: Number })
  markupCost: number

  @Field(() => Date)
  @Prop({ type: Date })
  date: Date

  @Field(() => String)
  @Prop({ type: String })
  trackingNumber: string

  @Field(() => String)
  @Prop({ type: String })
  trackingUrl: string

  @Field(() => String)
  @Prop({ type: String })
  pdfLabel: string

  @Field(() => String)
  @Prop({ type: String })
  pngLabel: string

  @Field(() => String)
  @Prop({ type: String })
  zplLabel: string

  @Field(() => String)
  @Prop({ type: String })
  hrefLabel: string

  @Field(() => String)
  @Prop({ type: String })
  pickupId: string

  @Field(() => Date)
  @Prop({ type: Date })
  pickupWindowStartDate: Date

  @Field(() => String)
  @Prop({ type: Date })
  pickupWindowEndDate: Date

  @Field(() => String)
  @Prop({ type: String })
  pickupNotes: string

  @Field(() => Date)
  @Prop({ type: Date })
  pickupCancelledDate: Date

  @Field(() => [ShippingPackage])
  @Prop({ type: [ShippingPackage] })
  packages: ShippingPackage[]

  @Field(() => Address)
  @Prop({ type: Address })
  shipFrom: Address

  @Field(() => Address)
  @Prop({ type: Address })
  shipTo: Address

  @Field(() => Boolean)
  @Prop({ type: Boolean, default: false })
  isVoidLabel: boolean

  @Field(() => String)
  @Prop({ type: String })
  voidMessage: string

  @Field(() => String)
  @Prop({ type: String })
  billToAccount: string

  @Field(() => String)
  @Prop({ type: String })
  billToCountryCode: string

  @Field(() => String)
  @Prop({ type: String })
  billToPostalCode: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  billToCustomer: boolean

  @Field(() => ID, { nullable: true })
  @Prop({ type: String })
  billToAccountOwnerId?: string

  @Field(() => String)
  @Prop({ type: String })
  insuranceClaimUrl: string

  @Field(() => Float)
  @Prop({ type: Number })
  insuredAmount: number

  @Field(() => String)
  @Prop({ type: String })
  deliveryConfirmation: OrderDeliveryConfirmationEnum

  @Field(() => [String])
  @Prop({ type: [String] })
  serviceErrors: string[] //put here the shipping errors returned by the services so we can track the states of it

  @Field(() => [String])
  @Prop({ type: [String] })
  serviceWarnings: string[]

  @Field(() => [String])
  @Prop({ type: [String] })
  itemQuantityErrors: string[]

  @Field(() => String)
  @Prop({ type: String })
  serviceCode: string

  @Field(() => GraphQLJSON)
  @Prop({ type: Metadata })
  metadata?: MetadataType

  @Field(() => String)
  @Prop({ type: String })
  formDownload: string

  @Field(() => ShippingCustoms)
  @Prop({ type: ShippingCustoms })
  customs: ShippingCustoms

  @Field(() => String)
  @Prop({ type: String })
  gatewayProvider: ShippingGatewayProvider
}
